import { Box, Flex, Image, Text, VStack } from "@chakra-ui/react";
import { Bundle } from "../../../types";
import { ShareButton } from "../../buttons/share/ShareButton";
import { ShowMoreSection } from "../../containers/ShowMoreSection";
import { useBundlePricing } from "../../../hooks/useBundlePricing";
import { BundleDetailsPricingInformation } from "./components/BundleDetailsPriceInformation";
import { BundleDetailsContributors } from "./components/BundleDetailsContributors";
import { BundleDetailsReleasesGrid } from "./components/BundleDetailsReleasesGrid";
import { BundleStats } from "./components/BundleStats";
import { BundleTagsSection } from "./components/BundleTagsSection";
import { useSlideInMenu } from "../hooks/useSlideInMenu";
import { FunnelConfigTypes } from "../../funnel/types";

interface BundleDetailsBodyProps {
  bundle: Bundle;
  setFunnelConfig?: (config?: FunnelConfigTypes) => void;
}

export function BundleDetailsBody({
  bundle,
  setFunnelConfig,
}: BundleDetailsBodyProps) {
  // Calculate discount from price data
  const { priceToUse, hasDiscount, discountPercentage } =
    useBundlePricing(bundle);

  // Get the first image from items for the background
  const backgroundImage = bundle.items?.[0]?.thumbnail_url;

  // Hook for opening artist profile
  const { openArtistProfile } = useSlideInMenu();

  const handleArtistClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent bubbling to parent onClick handlers
    if (bundle.owner_id) {
      openArtistProfile(bundle.owner_id);
    }
  };

  return (
    <Flex flexDirection="column" height="100%" overflow="hidden">
      <Box overflowY="auto" flex={1}>
        <VStack spacing={0} align="stretch">
          {/* Information Header with Background */}
          <Flex
            flexDirection="column"
            position="relative"
            bgSize="cover"
            bgPosition="center"
            pt="16px"
            px="16px"
            _after={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgGradient: backgroundImage
                ? "linear(to-b, rgba(0, 0, 0, 0.5), charcoal.charcoal)"
                : undefined,
              zIndex: 0,
            }}
          >
            {/* Top Row: Artist Image and Share Button */}
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              alignItems="flex-start"
              zIndex={2}
              mb="12px"
            >
              {bundle.owner?.avatar_url && (
                <Image
                  src={bundle.owner.avatar_url}
                  alt={`${bundle.owner.name} avatar`}
                  boxSize="48px"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="transparent.white.10"
                  boxShadow="0px 2px 4px 0px rgba(0, 0, 0, 0.3)"
                  cursor="pointer"
                  onClick={handleArtistClick}
                />
              )}

              <ShareButton
                shareUrl={bundle.url}
                title="Share Bundle"
                color="neutral.300"
              />
            </Flex>

            {/* Bundle Name */}
            <Text
              fontSize="24px"
              fontWeight="bold"
              color="neutral.white"
              lineHeight="130%"
              zIndex={1}
              mb="8px"
            >
              {bundle.display_name}
            </Text>

            {/* Bundle Description */}
            {bundle.description && (
              <Text
                color="neutral.200"
                fontSize="16px"
                lineHeight="1.5"
                zIndex={1}
                noOfLines={3}
                pb="16px"
              >
                {bundle.description}
              </Text>
            )}
            {/* Bundle pricing information */}
            <BundleDetailsPricingInformation
              bundle={bundle}
              priceToUse={priceToUse}
              hasDiscount={hasDiscount}
              discountPercentage={discountPercentage}
              setFunnelConfig={setFunnelConfig}
            />
          </Flex>

          {/* Rest of Content */}
          <VStack spacing="24px" px="24px" pt="24px" pb="24px">
            {/* Bundle Stats Section */}
            <BundleStats bundle={bundle} />
            {/* Separator */}
            <Box width="100%" height="1px" background="transparent.white.10" />

            {/* Bundle Tags and Contributors */}
            <ShowMoreSection
              collapsedHeight={60}
              expandedMarginBottom="24px"
              width="100%"
            >
              <VStack spacing="24px" align="start" width="100%">
                <BundleTagsSection bundle={bundle} hasDiscount={hasDiscount} />
                <BundleDetailsContributors bundle={bundle} />
              </VStack>
            </ShowMoreSection>

            {/* Bundle Releases Grid with Buttons */}
            <BundleDetailsReleasesGrid
              items={bundle.items}
              userOwnsAllItems={bundle.user_owns_all_items}
              area="bundle-details"
            />
          </VStack>
        </VStack>
      </Box>
    </Flex>
  );
}
